/// <amd-module name="Pipeline/Analytics/PipelineDurationHub/Pipeline.DurationHub" />
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { ContextType, IReportContext, Period, TrendBy } from "Analytics/Shared/Common/Types";
import { ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { getDefinitionIdFromUrl } from "Analytics/Shared/Utils/URLHelper";
import { PipelineAnalyticsPage } from "Pipeline/Analytics/Common/Components/PipelineAnalyticsPage";
import { enableSixMonthReportsFF } from "Pipeline/Analytics/Common/Constants";
import * as PerfScenarios from "Pipeline/Analytics/Common/PerfScenarios";
import { IPipelineAnalyticsTelemetryService } from "Pipeline/Analytics/Common/Telemetry/PipelineAnalyticsTelemetryService";
import * as TelemetryConstants from "Pipeline/Analytics/Common/Telemetry/TelemetryConstants";
import { DurationMetricsTrendCardActions } from "Pipeline/Analytics/DurationMetricsTrendCard/Actions/DurationMetricsTrendCardActions";
import { DurationMetricsTrendCardActionsCreator } from "Pipeline/Analytics/DurationMetricsTrendCard/Actions/DurationMetricsTrendCardActionsCreator";
import { DurationMetricsTrendCard } from "Pipeline/Analytics/DurationMetricsTrendCard/ControllerViews/DurationMetricsTrendCard";
import { DurationMetricsTrendCardStore } from "Pipeline/Analytics/DurationMetricsTrendCard/Stores/DurationMetricsTrendCardStore";
import { PipelineAnalyticsHeader } from "Pipeline/Analytics/PipelineAnalyticsHeader/Pipeline.AnalyticsHeader";
import { IPipelineDurationData } from "Pipeline/Analytics/PipelineDurationCommon/Types";
import * as Resources from "Pipeline/Analytics/PipelineDurationHub/Resources";
import { PipelineStepsDurationCardActions } from "Pipeline/Analytics/PipelineStepsDurationCard/Actions/PipelineStepsDurationCardActions";
import { PipelineStepsDurationCardActionsCreator } from "Pipeline/Analytics/PipelineStepsDurationCard/Actions/PipelineStepsDurationCardActionsCreator";
import { PipelineStepsDurationCard } from "Pipeline/Analytics/PipelineStepsDurationCard/ControllerViews/PipelineStepsDurationCard";
import { PipelineStepsDurationCardStore } from "Pipeline/Analytics/PipelineStepsDurationCard/Stores/PipelineStepsDurationCardStore";
import * as React from "react";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export interface IDurationPageState {
    definitionName: string;
    pipelineCount: number;
    errorText?: string;
}

export class PipelineDurationPage extends VssComponent<IVssComponentProperties, IDurationPageState> {
    private _reportContext: IReportContext;
    private _durationMetricsTrendCardStore: DurationMetricsTrendCardStore;
    private _durationMetricsTrendCardActionsCreator: DurationMetricsTrendCardActionsCreator;
    private _pipelineStepsDurationCardStore: PipelineStepsDurationCardStore;
    private _pipelineStepsDurationCardActionsCreator: PipelineStepsDurationCardActionsCreator;
    private _defaultPeriod: Period = Period.Days_14;

    private _hasDurationMetricsTrendCardRendered = false;
    private _hasPipelineStepsDurationCardRendered = false;
    private _reportRenderCount = 0;

    private _perfService: IVssPerformanceService;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        if (isFeatureFlagEnabled(context.pageContext, PipelineEntities.RenameBuildEntitiesFeatureFlag)) {
            PipelineEntities.switchEntityAndAttributeNames();
        }

        this.state = {} as IDurationPageState;
        this._perfService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        const durationMetricsTrendCardActions = new DurationMetricsTrendCardActions();
        this._durationMetricsTrendCardStore = new DurationMetricsTrendCardStore(durationMetricsTrendCardActions);
        this._durationMetricsTrendCardActionsCreator = new DurationMetricsTrendCardActionsCreator(
            durationMetricsTrendCardActions,
            context.pageContext
        );

        const pipelineStepsDurationCardActions = new PipelineStepsDurationCardActions();
        this._pipelineStepsDurationCardStore = new PipelineStepsDurationCardStore(pipelineStepsDurationCardActions);
        this._pipelineStepsDurationCardActionsCreator = new PipelineStepsDurationCardActionsCreator(
            pipelineStepsDurationCardActions,
            context.pageContext
        );

        const definitionId = this._getBuildDefinitionId();
        this._reportContext = {
            contextType: ContextType.Build,
            definitionId: definitionId
        } as IReportContext;
    }

    public componentWillUnmount() {
        this._durationMetricsTrendCardStore.dispose();
        this._pipelineStepsDurationCardStore.dispose();
    }

    public render() {
        return (
            <PipelineAnalyticsPage
                renderHeader={this._renderHeader}
                getDataValidationPromises={this._getDataValidationPromises}
                onSetPipelineName={this._handleSetPipelineName}
                onError={this._handleError}
            >
                <div className="pipeline-duration-report-view flex-grow flex-column">
                    <DurationMetricsTrendCard
                        className="pipeline-duration-metrics-card"
                        store={this._durationMetricsTrendCardStore}
                        onDataRendered={this._onDurationMetricsTrendCardRendered}
                        errorText={this.state.errorText}
                        updatePipelineDurationData={this._updatePipelineDurationData}
                    />
                    {this.state.pipelineCount > 0 && (
                        <PipelineStepsDurationCard
                            store={this._pipelineStepsDurationCardStore}
                            onDataRendered={this._onPipelineStepsDurationCardRendered}
                            errorText={this.state.errorText}
                            actionCreator={this._pipelineStepsDurationCardActionsCreator}
                            className="pipeline-steps-duration-card"
                            reportContext={this._reportContext}
                        />
                    )}
                </div>
            </PipelineAnalyticsPage>
        );
    }

    private _renderHeader = (): JSX.Element => {
        return (
            <PipelineAnalyticsHeader
                headerTitle={Resources.PipelineDurationReportHeaderText}
                definitionName={this.state.definitionName}
                definitionId={this._reportContext.definitionId}
                onPeriodChanged={this._onPeriodChanged}
                onBranchChanged={this._onBranchChanged}
                maxPeriod={isFeatureFlagEnabled(this.context.pageContext, enableSixMonthReportsFF, false) ? Period.Days_180 : Period.Days_30}
            />
        );
    };

    private _getDataValidationPromises = (): Promise<any>[] => {
        const confValues = {
            period: this._defaultPeriod,
            trendBy: TrendBy.Days
        } as ITrendChartReportConfiguration;
        const dataValidationPromises = this._durationMetricsTrendCardActionsCreator.updateConfigurationValues(this._reportContext, confValues);
        dataValidationPromises.concat(this._pipelineStepsDurationCardActionsCreator.updateConfigurationValues(this._reportContext, confValues));
        return dataValidationPromises;
    };

    private _handleSetPipelineName = (pipelineName: string) => {
        this.setState({ definitionName: pipelineName });
    };

    private _handleError = (errorText: string) => {
        this.setState({ errorText: errorText });
    };

    private _onDurationMetricsTrendCardRendered = () => {
        this._hasDurationMetricsTrendCardRendered = true;
        this._logPerformance();
    };

    private _onPipelineStepsDurationCardRendered = () => {
        this._hasPipelineStepsDurationCardRendered = true;
        this._logPerformance();
    };

    private _logPerformance() {
        if (this._hasDurationMetricsTrendCardRendered || this._hasPipelineStepsDurationCardRendered) {
            if (this._reportRenderCount === 0) {
                this._perfService.markTimeToInteractive(PerfScenarios.Area, PerfScenarios.PipelinesDurationHub);
                this._reportRenderCount++;
            }
        }
    }

    private _getBuildDefinitionId(): number {
        return getDefinitionIdFromUrl(this.context.pageContext);
    }

    private _publishDateFilterChangedTelemetry(period: Period): void {
        const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
        telemetryService.publishEventWithSingleKey(
            TelemetryConstants.feature_PipelineAnalyticsDurationHubFilter,
            TelemetryConstants.DateFilterChangedValue,
            Period[period]
        );
    }

    private _publishBranchFilterChangedTelemetry(confValues: ITrendChartReportConfiguration): void {
        const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
        telemetryService.publishEventWithSingleKey(
            TelemetryConstants.feature_PipelineAnalyticsDurationHubFilter,
            TelemetryConstants.BranchCount,
            confValues.branchItems!.length
        );
    }

    private _onPeriodChanged = (confValues: ITrendChartReportConfiguration) => {
        this._durationMetricsTrendCardActionsCreator.updateConfigurationValues(this._reportContext, confValues);
        this._pipelineStepsDurationCardActionsCreator.updateConfigurationValues(this._reportContext, confValues);
        this._publishDateFilterChangedTelemetry(confValues.period);
    };

    private _onBranchChanged = (confValues: ITrendChartReportConfiguration): void => {
        this._durationMetricsTrendCardActionsCreator.updateConfigurationValues(this._reportContext, confValues);
        this._pipelineStepsDurationCardActionsCreator.updateConfigurationValues(this._reportContext, confValues);
        this._publishBranchFilterChangedTelemetry(confValues);
    };

    private _updatePipelineDurationData = (data: IPipelineDurationData): void => {
        this.setState({
            pipelineCount: data.pipelineCount
        });
    };
}

Components.add("pipelineDurationHub", PipelineDurationPage);
